import { useCallback, useState } from "react"
import {
	ForgeRockStep,
	ForgeRockAuthService
} from "@services/forgeRockAuthService"
import { ForgeRockCallback } from "@models/forgeRock.model"
import { LoginError } from "@models/error/login-error"
import { FRStep } from "@forgerock/javascript-sdk"
import {
	MFACallbacks,
	extractCallbackMessage,
	isErrorMessage,
	parseForgeRockErrorMessage,
	detectMFACallbacks
} from "@utils/forgeRock.util"

interface ExtendedBooleanAttributeInputCallback extends ForgeRockCallback {
	setInputValue?: (value: boolean | string) => void
	input?: Array<{ value: boolean | string }>
	setValidateOnly?: (value: boolean) => void
}

interface UseMFASubmissionProps {
	currentStep: ForgeRockStep | null
	currentStepNumber: number
	mfaCallbacks: MFACallbacks | null
	onStepUpdate: (
		stepNumber: number,
		step: ForgeRockStep,
		callbackValues: Record<number, string>
	) => void
	onSuccess: () => void
	onError?: (error: Error) => void
	logStepInfo: (
		action: string,
		stepNumber: number,
		step?: ForgeRockStep,
		additionalData?: Record<string, unknown>
	) => void
}

export const useMFASubmission = ({
	currentStep,
	currentStepNumber,
	mfaCallbacks,
	onStepUpdate,
	onSuccess,
	onError,
	logStepInfo
}: UseMFASubmissionProps) => {
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [error, setError] = useState<LoginError | null>(null)

	/**
	 * Submits the MFA step with OTP code and remember device option
	 */
	const submitOTP = useCallback(
		async (otp: string, rememberDevice = true): Promise<void> => {
			if (!currentStep || !currentStep.callbacks || !mfaCallbacks) {
				throw new Error("MFA step or callbacks not available")
			}

			// Validate OTP format (should be 6 digits)
			if (!otp || otp.length !== 6 || !/^\d{6}$/.test(otp)) {
				const errorMessage = "Please enter a valid 6-digit code"
				setError(new LoginError(errorMessage, "MFA_OTP_INVALID_FORMAT", 0))
				throw new LoginError(errorMessage, "MFA_OTP_INVALID_FORMAT", 0)
			}

			setIsLoading(true)
			setError(null)

			try {
				// Prepare callback values map
				const callbackValues: Record<number, string> = {}

				// Set OTP value on PasswordCallback (this is the main OTP input)
				if (mfaCallbacks.otpCallback && mfaCallbacks.otpCallbackIndex >= 0) {
					callbackValues[mfaCallbacks.otpCallbackIndex] = otp
				}

				// Find ChoiceCallback manually if not detected
				let choiceCallbackIndex = mfaCallbacks.choiceCallbackIndex
				if (choiceCallbackIndex < 0) {
					currentStep.callbacks.forEach(
						(callback: ForgeRockCallback, index: number) => {
							const callbackType = callback.getType?.() || callback.type
							if (callbackType === "ChoiceCallback") {
								choiceCallbackIndex = index
							}
						}
					)
				}

				// Set choice to "Verify OTP" (index 0) on ChoiceCallback
				if (choiceCallbackIndex >= 0) {
					const verifyChoiceValue = "0" // "Verify OTP" is typically index 0
					callbackValues[choiceCallbackIndex] = verifyChoiceValue
				}

				// Find BooleanAttributeInputCallback manually if not detected
				// The detection might fail if the callback name doesn't match "remember" or "device"
				let booleanCallbackIndex = mfaCallbacks.rememberDeviceCallbackIndex
				if (booleanCallbackIndex < 0) {
					currentStep.callbacks.forEach(
						(callback: ForgeRockCallback, index: number) => {
							const callbackType = callback.getType?.() || callback.type
							if (callbackType === "BooleanAttributeInputCallback") {
								booleanCallbackIndex = index
							}
						}
					)
				}

				// Set values on ALL callbacks before submitting (following the same pattern as useForgeRockSubmission)
				// This ensures all callbacks have their values set, not just the ones we detected
				currentStep.callbacks.forEach(
					(callback: ForgeRockCallback, index: number) => {
						const callbackType = callback.getType?.() || callback.type

						// Skip output-only callbacks like TextOutputCallback
						if (callbackType !== "TextOutputCallback") {
							// Special handling for BooleanAttributeInputCallback - it requires boolean value, not string
							if (
								callbackType === "BooleanAttributeInputCallback" &&
								index === booleanCallbackIndex
							) {
								// BooleanAttributeInputCallback needs an actual boolean value, not a string
								// Cast to any to bypass TypeScript's string type restriction
								;(
									callback as ExtendedBooleanAttributeInputCallback
								).setInputValue?.(rememberDevice)
							} else if (
								callbackType === "ChoiceCallback" &&
								index === choiceCallbackIndex
							) {
								// Ensure ChoiceCallback is set correctly
								const verifyChoiceValue = "0"
								callback.setInputValue?.(verifyChoiceValue)
							} else {
								const value = callbackValues[index]
								if (value !== undefined) {
									// Set the value on the callback
									callback.setInputValue?.(value)
								}
							}
						}
					}
				)

				// Final verification: Ensure all callbacks have correct values right before submission
				if (choiceCallbackIndex >= 0) {
					const choiceCallback = currentStep.callbacks[choiceCallbackIndex]
					if (choiceCallback) {
						choiceCallback.setInputValue?.("0")
					}
				}

				if (booleanCallbackIndex >= 0) {
					const rememberDeviceCallback =
						currentStep.callbacks[booleanCallbackIndex]
					if (rememberDeviceCallback) {
						// BooleanAttributeInputCallback has two inputs:
						// - input[0] (IDToken3): the actual boolean value
						// - input[1] (IDToken3validateOnly): validation flag (usually false for submissions)

						// Set the main value (IDToken3) as boolean
						;(
							rememberDeviceCallback as ExtendedBooleanAttributeInputCallback
						).setInputValue?.(rememberDevice)

						// Try to set both input values directly on the input array
						// BooleanAttributeInputCallback has: input[0] (IDToken3) and input[1] (IDToken3validateOnly)
						const extendedCallback =
							rememberDeviceCallback as ExtendedBooleanAttributeInputCallback
						if (
							extendedCallback.input &&
							Array.isArray(extendedCallback.input)
						) {
							const inputArray = extendedCallback.input
							// Set IDToken3 to the checkbox value
							if (inputArray[0]) {
								inputArray[0].value = rememberDevice
							}
							// Set IDToken3validateOnly - typically false for submissions, but set to match checkbox if needed
							if (inputArray[1]) {
								// Try both: matching the checkbox value, or keeping it false
								// You can change this based on what your backend expects
								inputArray[1].value = rememberDevice // Match the checkbox value
								// Alternative: inputArray[1].value = false // Keep validateOnly as false
							}
						} else if (
							(rememberDeviceCallback as ExtendedBooleanAttributeInputCallback)
								.setValidateOnly
						) {
							// If there's a setValidateOnly method, use it
							;(
								rememberDeviceCallback as ExtendedBooleanAttributeInputCallback
							).setValidateOnly?.(rememberDevice)
						}

						if (
							(rememberDeviceCallback as ExtendedBooleanAttributeInputCallback)
								.input
						) {
						}
					}
				} else {
				}

				logStepInfo("SUBMITTING_MFA_OTP", currentStepNumber, currentStep, {
					hasOTP: !!mfaCallbacks.otpCallback,
					otpCallbackIndex: mfaCallbacks.otpCallbackIndex,
					hasChoice: choiceCallbackIndex >= 0,
					choiceCallbackIndex: choiceCallbackIndex,
					hasRememberDevice: booleanCallbackIndex >= 0,
					rememberDeviceCallbackIndex: booleanCallbackIndex,
					rememberDevice,
					otpLength: otp.length
				})

				// Submit step to ForgeRock
				const nextStep = await ForgeRockAuthService.submitStep(
					currentStep as unknown as FRStep
				)

				// Check for LoginFailure BEFORE updating parent to prevent modal close
				if (nextStep.type === "LoginFailure" || nextStep.payload?.code) {
					const errorPayload = nextStep.payload || {}
					const errorMessage =
						errorPayload.message || "Invalid OTP. Please try again."

					setError(new LoginError(errorMessage, "MFA_OTP_ERROR", 0))
					setIsLoading(false)
					throw new LoginError(errorMessage, "MFA_OTP_ERROR", 0)
				}

				const nextStepNumber = currentStepNumber + 1

				// Update parent component with new step
				onStepUpdate(nextStepNumber, nextStep, callbackValues)

				// Check for errors in TextOutputCallbacks (e.g., INVALID_OTP)
				if (nextStep.callbacks) {
					for (const callback of nextStep.callbacks) {
						const callbackType = callback.getType?.() || callback.type
						if (callbackType === "TextOutputCallback") {
							const message = extractCallbackMessage(callback)
							if (message && isErrorMessage(callback, message)) {
								const parsed = parseForgeRockErrorMessage(message)
								if (parsed.errorType && parsed.userFriendlyMessage) {
									const errorMessage = parsed.userFriendlyMessage
									setError(new LoginError(errorMessage, "MFA_OTP_ERROR", 0))
									setIsLoading(false)
									throw new LoginError(errorMessage, "MFA_OTP_ERROR", 0)
								}
							}
						}
					}
				}

				logStepInfo("MFA_OTP_SUBMITTED", nextStepNumber, nextStep, {
					previousStepNumber: currentStepNumber,
					isSuccess: nextStep.type === "LoginSuccess"
				})

				if (nextStep.type === "LoginSuccess") {
					onSuccess()
				} else {
					setIsLoading(false)
				}
			} catch (error) {
				const errorMessage =
					error instanceof LoginError
						? error.message
						: "Failed to verify OTP. Please try again."

				setError(new LoginError(errorMessage, "MFA_OTP_ERROR", 0))
				onError?.(error as Error)
				setIsLoading(false)
				throw error
			}
		},
		[
			currentStep,
			currentStepNumber,
			mfaCallbacks,
			onStepUpdate,
			onSuccess,
			onError,
			logStepInfo
		]
	)

	/**
	 * Resends the OTP code by submitting with "Resend OTP" choice
	 */
	const resendOTP = useCallback(async (): Promise<void> => {
		if (!currentStep || !currentStep.callbacks) {
			throw new Error("MFA step or callbacks not available")
		}

		setIsLoading(true)
		setError(null)

		try {
			// Re-detect MFA callbacks from current step in case they weren't detected before
			const detectedCallbacks = detectMFACallbacks(currentStep) || mfaCallbacks
			if (!detectedCallbacks) {
				throw new Error("MFA callbacks not found in step")
			}

			const callbackValues: Record<number, string> = {}

			// Find ChoiceCallback by iterating through callbacks if not detected
			let choiceCallbackIndex = detectedCallbacks.choiceCallbackIndex
			if (choiceCallbackIndex < 0) {
				// Manually find ChoiceCallback
				currentStep.callbacks.forEach(
					(callback: ForgeRockCallback, index: number) => {
						const callbackType = callback.getType?.() || callback.type
						if (callbackType === "ChoiceCallback") {
							choiceCallbackIndex = index
						}
					}
				)
			}

			// First, clear and set PasswordCallback to empty string for resend
			const otpCallbackIndex = detectedCallbacks.otpCallbackIndex
			if (otpCallbackIndex >= 0) {
				const otpCallback = currentStep.callbacks[otpCallbackIndex]
				if (otpCallback) {
					const emptyOtpValue = "" // Empty string for resend
					callbackValues[otpCallbackIndex] = emptyOtpValue
					// Clear and set the value on the callback object from currentStep
					otpCallback.setInputValue?.(emptyOtpValue)
				}
			}

			// Set choice to "Resend OTP" (index 1) on ChoiceCallback
			if (choiceCallbackIndex >= 0) {
				const choiceCallback = currentStep.callbacks[choiceCallbackIndex]
				if (choiceCallback) {
					const resendChoiceValue = "1" // "Resend OTP" is typically index 1
					callbackValues[choiceCallbackIndex] = resendChoiceValue
					// Set the value on the callback object from currentStep
					choiceCallback.setInputValue?.(resendChoiceValue)
				}
			}

			// Keep remember device value if present
			const rememberDeviceCallbackIndex =
				detectedCallbacks.rememberDeviceCallbackIndex
			if (rememberDeviceCallbackIndex >= 0) {
				const rememberDeviceCallback =
					currentStep.callbacks[rememberDeviceCallbackIndex]
				if (rememberDeviceCallback) {
					const currentValue =
						rememberDeviceCallback.getInputValue?.() || "false"
					const rememberValue = currentValue === "true" ? "true" : "false"
					callbackValues[rememberDeviceCallbackIndex] = rememberValue
					rememberDeviceCallback.setInputValue?.(rememberValue)
				}
			}

			// Final verification and setting right before submission
			// This ensures values are set correctly even if something reset them
			if (otpCallbackIndex >= 0) {
				const otpCallback = currentStep.callbacks[otpCallbackIndex]
				if (otpCallback) {
					otpCallback.setInputValue?.("")
				}
			}

			if (choiceCallbackIndex >= 0) {
				const choiceCallback = currentStep.callbacks[choiceCallbackIndex]
				if (choiceCallback) {
					choiceCallback.setInputValue?.("1")
				}
			}

			logStepInfo("RESENDING_MFA_OTP", currentStepNumber, currentStep, {
				hasOTP: otpCallbackIndex >= 0,
				otpCallbackIndex: otpCallbackIndex,
				hasChoice: choiceCallbackIndex >= 0,
				hasRememberDevice: rememberDeviceCallbackIndex >= 0,
				otpValue:
					currentStep.callbacks[otpCallbackIndex]?.getInputValue?.() || "",
				choiceValue:
					currentStep.callbacks[choiceCallbackIndex]?.getInputValue?.() || "",
				choiceCallbackIndex: choiceCallbackIndex
			})

			// Submit step to ForgeRock - values should be set correctly now
			const nextStep = await ForgeRockAuthService.submitStep(
				currentStep as unknown as FRStep
			)

			// After resending, we typically get back the same MFA step with a new OTP
			const nextStepNumber = currentStepNumber + 1

			// Update parent component with new step
			onStepUpdate(nextStepNumber, nextStep, callbackValues)

			logStepInfo("MFA_OTP_RESENT", nextStepNumber, nextStep)

			setIsLoading(false)
		} catch (error) {
			const errorMessage = "Failed to resend OTP. Please try again."
			setError(new LoginError(errorMessage, "MFA_RESEND_ERROR", 0))
			onError?.(error as Error)
			setIsLoading(false)
			throw error
		}
	}, [
		currentStep,
		currentStepNumber,
		mfaCallbacks,
		onStepUpdate,
		onError,
		logStepInfo
	])

	/**
	 * Sends SMS code by submitting with "Send SMS" choice
	 */
	const sendSMS = useCallback(async (): Promise<void> => {
		if (!currentStep || !currentStep.callbacks) {
			throw new Error("MFA step or callbacks not available")
		}

		setIsLoading(true)
		setError(null)

		try {
			// Re-detect MFA callbacks from current step in case they weren't detected before
			const detectedCallbacks = detectMFACallbacks(currentStep) || mfaCallbacks
			if (!detectedCallbacks) {
				throw new Error("MFA callbacks not found in step")
			}

			const callbackValues: Record<number, string> = {}

			// Find ChoiceCallback by iterating through callbacks if not detected
			let choiceCallbackIndex = detectedCallbacks.choiceCallbackIndex
			if (choiceCallbackIndex < 0) {
				// Manually find ChoiceCallback
				currentStep.callbacks.forEach(
					(callback: ForgeRockCallback, index: number) => {
						const callbackType = callback.getType?.() || callback.type
						if (callbackType === "ChoiceCallback") {
							choiceCallbackIndex = index
						}
					}
				)
			}

			// First, clear and set PasswordCallback to empty string for SMS send
			const otpCallbackIndex = detectedCallbacks.otpCallbackIndex
			if (otpCallbackIndex >= 0) {
				const otpCallback = currentStep.callbacks[otpCallbackIndex]
				if (otpCallback) {
					const emptyOtpValue = "" // Empty string for SMS send
					callbackValues[otpCallbackIndex] = emptyOtpValue
					// Clear and set the value on the callback object from currentStep
					otpCallback.setInputValue?.(emptyOtpValue)
				}
			}

			// Set choice to "Send SMS" (index 2) on ChoiceCallback
			if (choiceCallbackIndex >= 0) {
				const choiceCallback = currentStep.callbacks[choiceCallbackIndex]
				if (choiceCallback) {
					const smsChoiceValue = "2" // "Send SMS" is typically index 2
					callbackValues[choiceCallbackIndex] = smsChoiceValue
					// Set the value on the callback object from currentStep
					choiceCallback.setInputValue?.(smsChoiceValue)
				}
			}

			// Keep remember device value if present
			const rememberDeviceCallbackIndex =
				detectedCallbacks.rememberDeviceCallbackIndex
			if (rememberDeviceCallbackIndex >= 0) {
				const rememberDeviceCallback =
					currentStep.callbacks[rememberDeviceCallbackIndex]
				if (rememberDeviceCallback) {
					const currentValue =
						rememberDeviceCallback.getInputValue?.() || "false"
					const rememberValue = currentValue === "true" ? "true" : "false"
					callbackValues[rememberDeviceCallbackIndex] = rememberValue
					rememberDeviceCallback.setInputValue?.(rememberValue)
				}
			}

			// Final verification and setting right before submission
			// This ensures values are set correctly even if something reset them
			if (otpCallbackIndex >= 0) {
				const otpCallback = currentStep.callbacks[otpCallbackIndex]
				if (otpCallback) {
					otpCallback.setInputValue?.("")
				}
			}

			if (choiceCallbackIndex >= 0) {
				const choiceCallback = currentStep.callbacks[choiceCallbackIndex]
				if (choiceCallback) {
					choiceCallback.setInputValue?.("2")
				}
			}

			logStepInfo("SENDING_MFA_SMS", currentStepNumber, currentStep, {
				hasOTP: otpCallbackIndex >= 0,
				otpCallbackIndex: otpCallbackIndex,
				hasChoice: choiceCallbackIndex >= 0,
				hasRememberDevice: rememberDeviceCallbackIndex >= 0,
				otpValue:
					currentStep.callbacks[otpCallbackIndex]?.getInputValue?.() || "",
				choiceValue:
					currentStep.callbacks[choiceCallbackIndex]?.getInputValue?.() || "",
				choiceCallbackIndex: choiceCallbackIndex
			})

			// Submit step to ForgeRock - values should be set correctly now
			const nextStep = await ForgeRockAuthService.submitStep(
				currentStep as unknown as FRStep
			)

			// After sending SMS, we typically get back the same MFA step with a new OTP via SMS
			const nextStepNumber = currentStepNumber + 1

			// Update parent component with new step
			onStepUpdate(nextStepNumber, nextStep, callbackValues)

			logStepInfo("MFA_SMS_SENT", nextStepNumber, nextStep)

			setIsLoading(false)
		} catch (error) {
			const errorMessage = "Failed to send SMS. Please try again."
			setError(new LoginError(errorMessage, "MFA_SMS_ERROR", 0))
			onError?.(error as Error)
			setIsLoading(false)
			throw error
		}
	}, [
		currentStep,
		currentStepNumber,
		mfaCallbacks,
		onStepUpdate,
		onError,
		logStepInfo
	])

	return {
		submitOTP,
		resendOTP,
		sendSMS,
		isLoading,
		error,
		setError
	}
}
